import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";

import QRCode from "qrcode";
import logo from "./zulu.png";

// ==============================
// STYLES (CLEAN INVOICE DESIGN)
// ==============================
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
    color: "#111827",
    backgroundColor: "#ffffff",
  },

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "2 solid #111827",
    paddingBottom: 12,
    marginBottom: 15,
  },

  logo: {
    width: 60,
    height: 60,
  },

  title: {
    fontSize: 18,
    fontWeight: "bold",
  },

  subtitle: {
    fontSize: 10,
    marginTop: 2,
    color: "#6B7280",
  },

  section: {
    marginBottom: 12,
  },

  sectionTitle: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 6,
    padding: 6,
    backgroundColor: "#F3F4F6",
    borderLeft: "3 solid #111827",
  },

  grid: {
    flexDirection: "row",
    justifyContent: "space-between",
    border: "1 solid #E5E7EB",
    padding: 10,
    marginBottom: 10,
  },

  col: {
    width: "48%",
  },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },

  label: {
    color: "#6B7280",
    width: "40%",
  },

  value: {
    fontWeight: "bold",
    width: "60%",
    textAlign: "right",
  },

  table: {
    width: "100%",
    border: "1 solid #E5E7EB",
    marginTop: 8,
  },

  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#111827",
    color: "#fff",
    padding: 6,
  },

  th: {
    fontSize: 10,
    fontWeight: "bold",
  },

  tr: {
    flexDirection: "row",
    borderTop: "1 solid #E5E7EB",
    padding: 6,
  },

  td: {
    fontSize: 9,
  },

  col1: { width: "55%" },
  col2: { width: "20%" },
  col3: { width: "25%" },

  summaryBox: {
    marginTop: 12,
    border: "1 solid #E5E7EB",
    padding: 10,
    width: "45%",
    marginLeft: "auto",
    backgroundColor: "#F9FAFB",
  },

  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },

  total: {
    fontWeight: "bold",
    fontSize: 11,
    marginTop: 5,
    borderTop: "1 solid #E5E7EB",
    paddingTop: 5,
  },

  footer: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderTop: "1 solid #E5E7EB",
    paddingTop: 8,
  },

  qr: {
    width: 70,
    height: 70,
  },
});

// ==============================
// DOCUMENT
// ==============================
const ShipmentReceipt = ({ shipment }) => {
  const [qr, setQr] = React.useState("");

  React.useEffect(() => {
    if (!shipment) return;

    const qrData = JSON.stringify({
      tracking_code: shipment.tracking_code,
      sender: shipment.customer_name,
      receiver: shipment.receiver_name,
      status: shipment.tracking_status,
    });

    QRCode.toDataURL(qrData).then(setQr);
  }, [shipment]);

  if (!shipment) return null;

  const total =
    Number(shipment.amount_charge || 0) -
    Number(shipment.discount || 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* HEADER */}
        <View style={styles.header}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Image src={logo} style={styles.logo} />

            <View style={{ marginLeft: 10 }}>
              <Text style={styles.title}>SHIPMENT INVOICE</Text>
              <Text style={styles.subtitle}>
                Tracking Code: {shipment.tracking_code}
              </Text>
              <Text style={styles.subtitle}>
                Status: {shipment.tracking_status}
              </Text>
            </View>
          </View>
        </View>

        {/* SENDER / RECEIVER */}
        <View style={styles.grid}>

          {/* SENDER */}
          <View style={styles.col}>
            <Text style={styles.sectionTitle}>Sender Information</Text>

            <View style={styles.row}>
              <Text style={styles.label}>Name</Text>
              <Text style={styles.value}>{shipment.customer_name}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Email</Text>
              <Text style={styles.value}>{shipment.email}</Text>
            </View>
          </View>

          {/* RECEIVER */}
          <View style={styles.col}>
            <Text style={styles.sectionTitle}>Receiver Information</Text>

            <View style={styles.row}>
              <Text style={styles.label}>Name</Text>
              <Text style={styles.value}>{shipment.receiver_name}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Email</Text>
              <Text style={styles.value}>
                {shipment.receiver_email || "N/A"}
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Contact</Text>
              <Text style={styles.value}>
                {shipment.receiver_contact}
              </Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Address</Text>
              <Text style={styles.value}>
                {shipment.receiver_address}
              </Text>
            </View>
          </View>

        </View>

        {/* ITEMS TABLE */}
        <Text style={styles.sectionTitle}>Shipment Details</Text>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.th, styles.col1]}>Item Description</Text>
            <Text style={[styles.th, styles.col2]}>Charge</Text>
            <Text style={[styles.th, styles.col3]}>Discount</Text>
          </View>

          <View style={styles.tr}>
            <Text style={[styles.td, styles.col1]}>
              {shipment.items_name}
            </Text>

            <Text style={[styles.td, styles.col2]}>
              $USD {shipment.amount_charge}
            </Text>

            <Text style={[styles.td, styles.col3]}>
              $USD {shipment.discount}
            </Text>
          </View>
        </View>

        {/* SUMMARY */}
        <View style={styles.summaryBox}>
          <View style={styles.summaryRow}>
            <Text>Subtotal:</Text>
            <Text>$USD {shipment.amount_charge}</Text>
          </View>

          <View style={styles.summaryRow}>
            <Text>Discount:</Text>
            <Text>-$USD {shipment.discount}</Text>
          </View>

          <View style={[styles.summaryRow, styles.total]}>
            <Text>Total:</Text>
            <Text>$USD {total}</Text>
          </View>
        </View>

        {/* FOOTER */}
        <View style={styles.footer}>
          <Text>Zuluwhiskey Shipping System</Text>
          {qr && <Image src={qr} style={styles.qr} />}
        </View>

      </Page>
    </Document>
  );
};

export default ShipmentReceipt;

// import React from "react";
// import {
//   Page,
//   Text,
//   View,
//   Document,
//   StyleSheet,
//   Image,
// } from "@react-pdf/renderer";

// import QRCode from "qrcode";
// import logo from "./zulu.png";

// // ==============================
// // STYLES (CLEAN INVOICE DESIGN)
// // ==============================
// const styles = StyleSheet.create({
//   page: {
//     padding: 30,
//     fontSize: 10,
//     fontFamily: "Helvetica",
//     color: "#111827",
//     backgroundColor: "#ffffff",
//   },

//   header: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     borderBottom: "2 solid #111827",
//     paddingBottom: 12,
//     marginBottom: 15,
//   },

//   logo: {
//     width: 60,
//     height: 60,
//   },

//   title: {
//     fontSize: 18,
//     fontWeight: "bold",
//   },

//   subtitle: {
//     fontSize: 10,
//     marginTop: 2,
//     color: "#6B7280",
//   },

//   section: {
//     marginBottom: 12,
//   },

//   sectionTitle: {
//     fontSize: 11,
//     fontWeight: "bold",
//     marginBottom: 6,
//     padding: 6,
//     backgroundColor: "#F3F4F6",
//     borderLeft: "3 solid #111827",
//   },

//   grid: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     border: "1 solid #E5E7EB",
//     padding: 10,
//     marginBottom: 10,
//   },

//   col: {
//     width: "48%",
//   },

//   row: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     marginBottom: 4,
//   },

//   label: {
//     color: "#6B7280",
//     width: "40%",
//   },

//   value: {
//     fontWeight: "bold",
//     width: "60%",
//     textAlign: "right",
//   },

//   table: {
//     width: "100%",
//     border: "1 solid #E5E7EB",
//     marginTop: 8,
//   },

//   tableHeader: {
//     flexDirection: "row",
//     backgroundColor: "#111827",
//     color: "#fff",
//     padding: 6,
//   },

//   th: {
//     fontSize: 10,
//     fontWeight: "bold",
//   },

//   tr: {
//     flexDirection: "row",
//     borderTop: "1 solid #E5E7EB",
//     padding: 6,
//   },

//   td: {
//     fontSize: 9,
//   },

//   col1: { width: "55%" },
//   col2: { width: "20%" },
//   col3: { width: "25%" },

//   summaryBox: {
//     marginTop: 12,
//     border: "1 solid #E5E7EB",
//     padding: 10,
//     width: "45%",
//     marginLeft: "auto",
//     backgroundColor: "#F9FAFB",
//   },

//   summaryRow: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     marginBottom: 4,
//   },

//   total: {
//     fontWeight: "bold",
//     fontSize: 11,
//     marginTop: 5,
//     borderTop: "1 solid #E5E7EB",
//     paddingTop: 5,
//   },

//   footer: {
//     position: "absolute",
//     bottom: 20,
//     left: 30,
//     right: 30,
//     flexDirection: "row",
//     justifyContent: "space-between",
//     alignItems: "center",
//     borderTop: "1 solid #E5E7EB",
//     paddingTop: 8,
//   },

//   qr: {
//     width: 70,
//     height: 70,
//   },
// });

// // ==============================
// // DOCUMENT
// // ==============================
// const ShipmentReceipt = ({ shipment }) => {
//   const [qr, setQr] = React.useState("");

//   React.useEffect(() => {
//     if (!shipment) return;

//     const qrData = JSON.stringify({
//       tracking_code: shipment.tracking_code,
//       sender: shipment.customer_name,
//       receiver: shipment.receiver_name,
//       status: shipment.tracking_status,
//     });

//     QRCode.toDataURL(qrData).then(setQr);
//   }, [shipment]);

//   if (!shipment) return null;

//   const total =
//     Number(shipment.amount_charge || 0) -
//     Number(shipment.discount || 0);

//   return (
//     <Document>
//       <Page size="A4" style={styles.page}>

//         {/* HEADER */}
//         <View style={styles.header}>
//           <View style={{ flexDirection: "row", alignItems: "center" }}>
//             <Image src={logo} style={styles.logo} />

//             <View style={{ marginLeft: 10 }}>
//               <Text style={styles.title}>SHIPMENT INVOICE</Text>
//               <Text style={styles.subtitle}>
//                 Tracking Code: {shipment.tracking_code}
//               </Text>
//               <Text style={styles.subtitle}>
//                 Status: {shipment.tracking_status}
//               </Text>
//             </View>
//           </View>
//         </View>

//         {/* SENDER / RECEIVER */}
//         <View style={styles.grid}>

//           {/* SENDER */}
//           <View style={styles.col}>
//             <Text style={styles.sectionTitle}>Sender Information</Text>

//             <View style={styles.row}>
//               <Text style={styles.label}>Name</Text>
//               <Text style={styles.value}>{shipment.customer_name}</Text>
//             </View>

//             <View style={styles.row}>
//               <Text style={styles.label}>Email</Text>
//               <Text style={styles.value}>{shipment.email}</Text>
//             </View>
//           </View>

//           {/* RECEIVER */}
//           <View style={styles.col}>
//             <Text style={styles.sectionTitle}>Receiver Information</Text>

//             <View style={styles.row}>
//               <Text style={styles.label}>Name</Text>
//               <Text style={styles.value}>{shipment.receiver_name}</Text>
//             </View>

//             <View style={styles.row}>
//               <Text style={styles.label}>Email</Text>
//               <Text style={styles.value}>
//                 {shipment.receiver_email || "N/A"}
//               </Text>
//             </View>

//             <View style={styles.row}>
//               <Text style={styles.label}>Contact</Text>
//               <Text style={styles.value}>
//                 {shipment.receiver_contact}
//               </Text>
//             </View>

//             <View style={styles.row}>
//               <Text style={styles.label}>Address</Text>
//               <Text style={styles.value}>
//                 {shipment.receiver_address}
//               </Text>
//             </View>
//           </View>

//         </View>

//         {/* ITEMS TABLE */}
//         <Text style={styles.sectionTitle}>Shipment Details</Text>

//         <View style={styles.table}>
//           <View style={styles.tableHeader}>
//             <Text style={[styles.th, styles.col1]}>Item Description</Text>
//             <Text style={[styles.th, styles.col2]}>Charge</Text>
//             <Text style={[styles.th, styles.col3]}>Discount</Text>
//           </View>

//           <View style={styles.tr}>
//             <Text style={[styles.td, styles.col1]}>
//               {shipment.items_name}
//             </Text>
//             <Text style={[styles.td, styles.col2]}>
//               ${shipment.amount_charge}
//             </Text>
//             <Text style={[styles.td, styles.col3]}>
//               ${shipment.discount}
//             </Text>
//           </View>
//         </View>

//         {/* SUMMARY */}
//         <View style={styles.summaryBox}>
//           <View style={styles.summaryRow}>
//             <Text>Subtotal:</Text>
//             <Text>${shipment.amount_charge}</Text>
//           </View>

//           <View style={styles.summaryRow}>
//             <Text>Discount:</Text>
//             <Text>-${shipment.discount}</Text>
//           </View>

//           <View style={[styles.summaryRow, styles.total]}>
//             <Text>Total:</Text>
//             <Text>${total}</Text>
//           </View>
//         </View>

//         {/* FOOTER */}
//         <View style={styles.footer}>
//           <Text>Zuluwhiskey Shipping System</Text>
//           {qr && <Image src={qr} style={styles.qr} />}
//         </View>

//       </Page>
//     </Document>
//   );
// };

// export default ShipmentReceipt;
